import {
  DeletionStatus,
  DeletionTrigger,
  SearchDeletedOrgsRequest,
  SearchDeletedOrgsResponse,
} from "@safetyculture/s12-apis-connect-web/s12/accounts/history/v1/history_pb";
import {
  SearchIdentitiesFilters,
  SearchIdentitiesRequest,
  SearchIdentitiesResponse,
} from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/identity_management_pb";
import {
  ListOrganisationsBySensorIDRequest,
  ListOrganisationsBySensorIDResponse,
} from "@safetyculture/s12-apis-connect-web/s12/iot/admin/v1/sensors_pb";
import {
  ListOrganisationsByGatewayIDRequest,
  ListOrganisationsByGatewayIDResponse,
} from "@safetyculture/s12-apis-connect-web/s12/sensors/admin/v1/sensors_pb";
import {
  SearchTemplatesRequest,
  SearchTemplatesResponse,
} from "@safetyculture/s12-apis-connect-web/s12/templates/admin/v1/template_library_pb";
import { GetTemplateVersionRawRequest } from "@safetyculture/s12-apis-connect-web/s12/templates/admin/v1/templates_pb";
import { SearchTemplatesCriteria } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_library_pb";
import {
  GetMediaMetadataRequest,
  GetMediaMetadataResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/media_metadata_pb";
import {
  EmailSearch,
  GetDocumentRawRequest,
  Pagination,
  RemoveReportsCacheRequest,
  RemoveReportsCacheResponse,
  SearchOrgsRequest,
  SearchOrgsResponse,
  SearchUsersRequest,
  SearchUsersResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

export type SearchOrganizationParams = {
  pageSize: number;
  offset: number;
  orgName?: string;
  roleId?: string;
  sortByMembers?: boolean;
};
export const searchOrganization = async (
  {
    pageSize,
    offset,
    orgName,
    roleId,
    sortByMembers,
  }: SearchOrganizationParams,
  headers: Record<RequestMetadata, string>,
): Promise<SearchOrgsResponse> => {
  const pagination = new Pagination({
    pageSize: pageSize,
    offset: offset,
  });

  const req = new SearchOrgsRequest({
    name: orgName?.trim() || "",
    id: roleId?.trim() || "",
    pagination: pagination,
    sortByMembers: !!sortByMembers,
  });

  return supportServiceConnectClient.searchOrgs(req, { headers });
};

export type SearchUsersParams = {
  pageSize: number;
  offset: number;
  firstName?: string;
  lastName?: string;
  emailLocal?: string;
  emailDomain?: string;
  userId?: string;
  matchExactEmail?: boolean;
};

export const searchUsers = async (
  {
    pageSize,
    offset,
    firstName,
    lastName,
    emailLocal,
    emailDomain,
    userId,
    matchExactEmail,
  }: SearchUsersParams,
  headers: Record<RequestMetadata, string>,
): Promise<SearchUsersResponse> => {
  const pagination = new Pagination({
    pageSize: pageSize,
    offset: offset,
  });

  const emailSearch = new EmailSearch({
    local: emailLocal?.trim() || "",
    domain: emailDomain?.trim() || "",
    matchExactEmail: !!matchExactEmail,
  });

  const req = new SearchUsersRequest({
    firstName: firstName?.trim() || "",
    lastName: lastName?.trim() || "",
    id: userId?.trim() || "",
    pagination: pagination,
  });
  if (emailLocal || emailDomain) {
    req.email = emailSearch;
  }
  return supportServiceConnectClient.searchUsers(req, { headers });
};

export type GetDocumentRawParams = {
  docId: string;
  docType: string;
  revId: string;
  useRevId: boolean;
  includeDeleted: boolean;
  orgId?: string;
};

export const getDocumentRaw = async (
  {
    docId,
    docType,
    revId,
    useRevId,
    includeDeleted,
    orgId,
  }: GetDocumentRawParams,
  headers: Record<RequestMetadata, string>,
): Promise<string> => {
  // check
  if (docType === "template" && useRevId && revId) {
    const responseJson = (
      await supportServiceConnectClient.getTemplateVersionRaw(
        new GetTemplateVersionRawRequest({
          templateId: docId,
          revisionId: revId,
        }),
        { headers },
      )
    ).templateJson;
    return responseJson;
  } else if (docType === "responseset") {
    return (
      await supportServiceConnectClient.getDocumentRaw(
        new GetDocumentRawRequest({
          docId,
          docType,
          orgId,
        }),
        { headers },
      )
    ).rawDocument;
  }

  return (
    await supportServiceConnectClient.getDocumentRaw(
      new GetDocumentRawRequest({
        docId,
        docType,
        includeDeleted,
      }),
      { headers },
    )
  ).rawDocument;
};

type SearchPublicLibraryParams = {
  query?: string;
  ranking?: string;
  pageNum?: number;
  pageSize?: number;
};

export const searchPublicLibrary = async (
  { query, ranking, pageNum, pageSize }: SearchPublicLibraryParams,
  headers: Record<RequestMetadata, string>,
): Promise<SearchTemplatesResponse> => {
  const req = new SearchTemplatesRequest();
  const searchTemplateCriteria = new SearchTemplatesCriteria();

  if (query) {
    searchTemplateCriteria.query = query;
  }

  if (ranking) {
    searchTemplateCriteria.ranking = ranking;
  }

  searchTemplateCriteria.pageNum = pageNum || 1;
  searchTemplateCriteria.pageSize = pageSize || 25;

  req.searchTemplatesCriteria = searchTemplateCriteria;
  return supportServiceConnectClient.searchTemplates(req, { headers });
};

type SearchOrgsBySensorIdParams = {
  sensorId: string;
};

export const searchOrgsBySensorId = async (
  { sensorId }: SearchOrgsBySensorIdParams,
  headers: Record<RequestMetadata, string>,
): Promise<ListOrganisationsBySensorIDResponse> => {
  const req = new ListOrganisationsBySensorIDRequest({
    sensorId,
  });

  return supportServiceConnectClient.listOrganisationsBySensorID(req, {
    headers,
  });
};

type SearchOrgsByGatewayIdParams = {
  gatewayId: string;
};

export const searchOrgsByGatewayId = async (
  { gatewayId }: SearchOrgsByGatewayIdParams,
  headers: Record<RequestMetadata, string>,
): Promise<ListOrganisationsByGatewayIDResponse> => {
  const req = new ListOrganisationsByGatewayIDRequest({
    gatewayId,
  });

  return supportServiceConnectClient.listOrganisationsByGatewayID(req, {
    headers,
  });
};

type GetMediaMetadataParams = {
  mediaId: string;
};

export const getMediaMetadata = async (
  { mediaId }: GetMediaMetadataParams,
  headers: Record<RequestMetadata, string>,
): Promise<GetMediaMetadataResponse> => {
  const req = new GetMediaMetadataRequest({
    mediaId,
  });

  return supportServiceConnectClient.getMediaMetadata(req, { headers });
};

type RemoveReportsCacheParams = {
  inspectionId: string;
};

export const removeReportsCache = async (
  { inspectionId }: RemoveReportsCacheParams,
  headers: Record<RequestMetadata, string>,
): Promise<RemoveReportsCacheResponse> => {
  const req = new RemoveReportsCacheRequest({
    inspectionId,
  });

  return supportServiceConnectClient.removeReportsCache(req, { headers });
};

export type SearchIdentitiesParams = {
  filters: SearchIdentitiesFilters;
  pageSize: number;
  offset: number;
};

export const searchIdentities = async (
  { filters, pageSize, offset }: SearchIdentitiesParams,
  headers: Record<RequestMetadata, string>,
): Promise<SearchIdentitiesResponse> => {
  const req = new SearchIdentitiesRequest({
    filters,
    pageSize,
    offset,
  });

  return supportServiceConnectClient.searchIdentities(req, { headers });
};

export type SearchDeletedOrgsParams = {
  pageSize: number;
  pageToken?: string;
  orgId?: string;
  nameContains?: string;
  emailDomain?: string;
  deletionStatus?: DeletionStatus;
  deletedTrigger?: DeletionTrigger;
};

export const searchDeletedOrgs = async (
  {
    pageSize,
    pageToken,
    orgId,
    nameContains,
    emailDomain,
    deletionStatus,
    deletedTrigger,
  }: SearchDeletedOrgsParams,
  headers: Record<RequestMetadata, string>,
): Promise<SearchDeletedOrgsResponse> => {
  const req = new SearchDeletedOrgsRequest({
    orgId: orgId?.trim() || "",
    nameContains: nameContains?.trim() || "",
    emailDomain: emailDomain?.trim() || "",
    deletionStatus: deletionStatus ?? DeletionStatus.UNSPECIFIED,
    deletedTrigger: deletedTrigger ?? DeletionTrigger.UNSPECIFIED,
    pageSize,
    pageToken: pageToken || "",
  });

  return supportServiceConnectClient.searchDeletedOrgs(req, { headers });
};
